import { useState } from "react";
import MessageText from "../../chats/components/MessageText";

const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return "";
  const units = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return `${(bytes / Math.pow(1024, i)).toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
};

// Some backend records store a relative/partial path instead of the full
// Firebase Storage download URL. A relative path silently resolves against
// this app's own origin (e.g. localhost:3000/chat_media/...) and 404s, so
// treat anything that isn't a real absolute URL as unusable rather than
// rendering a link that looks clickable but never works.
const isAbsoluteUrl = (uri) => typeof uri === "string" && /^https?:\/\//i.test(uri);

// Fallback shown when a media URL is missing, malformed, or fails to load,
// instead of letting the browser render its default broken-image/broken-link markup.
const AttachmentFallback = ({ uri, name, size, icon, t }) => {
  const displayName = name || (uri ? uri.split("/").pop() : null);

  if (!displayName) {
    return <span className="text-muted fst-italic">{t("unsupportedMessageType")}</span>;
  }

  const content = (
    <>
      <i className={`fas ${icon}`}></i>
      <div className="attachment-info">
        <div className="attachment-name">{displayName}</div>
        {size ? <div className="attachment-size">{formatFileSize(size)}</div> : null}
      </div>
    </>
  );

  if (!isAbsoluteUrl(uri)) {
    return (
      <div className="attachment-card" style={{ opacity: 0.6, cursor: "not-allowed" }} title={t("mediaUnavailable")}>
        {content}
      </div>
    );
  }

  return (
    <a href={uri} target="_blank" rel="noopener noreferrer" className="attachment-card">
      {content}
    </a>
  );
};

const ImageMessage = ({ msg, t }) => {
  const [failed, setFailed] = useState(false);

  if (!isAbsoluteUrl(msg.uri) || failed) {
    return <AttachmentFallback uri={msg.uri} name={msg.name} size={msg.size} icon="fa-image" t={t} />;
  }

  return (
    <a href={msg.uri} target="_blank" rel="noopener noreferrer">
      <img
        src={msg.uri}
        alt={msg.name || "image"}
        className="chat-media-image"
        style={{ maxWidth: 220, borderRadius: 8 }}
        onError={() => setFailed(true)}
      />
    </a>
  );
};

const VideoMessage = ({ msg, t }) => {
  const [failed, setFailed] = useState(false);

  if (!isAbsoluteUrl(msg.uri) || failed) {
    return <AttachmentFallback uri={msg.uri} name={msg.name} size={msg.size} icon="fa-video" t={t} />;
  }

  return (
    <video
      src={msg.uri}
      controls
      style={{ maxWidth: 240, borderRadius: 8 }}
      onError={() => setFailed(true)}
    />
  );
};

const VoiceMessage = ({ msg, t }) => {
  const [failed, setFailed] = useState(false);

  if (!isAbsoluteUrl(msg.uri) || failed) {
    return <AttachmentFallback uri={msg.uri} name={msg.name} size={msg.size} icon="fa-microphone" t={t} />;
  }

  return (
    <div className="chat-voice-message">
      <span className="d-block small text-muted mb-1">{t("voiceMessage")}</span>
      <audio src={msg.uri} controls onError={() => setFailed(true)} />
    </div>
  );
};

const MessageContent = ({ msg, t }) => {
  switch (msg.type) {
    case "text":
      return <MessageText text={msg.text} />;

    case "image":
      return <ImageMessage msg={msg} t={t} />;

    case "video":
      return <VideoMessage msg={msg} t={t} />;

    case "voice":
      return <VoiceMessage msg={msg} t={t} />;

    case "file":
      return (
        <AttachmentFallback uri={msg.uri} name={msg.name} size={msg.size} icon="fa-file" t={t} />
      );

    case "location":
      if (msg.latitude == null || msg.longitude == null) {
        return <span className="text-muted fst-italic">{t("unsupportedMessageType")}</span>;
      }
      return (
        <a
          href={`https://www.google.com/maps?q=${msg.latitude},${msg.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="attachment-card"
        >
          <i className="fas fa-location-dot"></i>
          <div className="attachment-info">
            <div className="attachment-name">{msg.placeName || t("openInMaps")}</div>
          </div>
        </a>
      );

    default:
      return <span className="text-muted fst-italic">{t("unsupportedMessageType")}</span>;
  }
};

export default MessageContent;
